// Replace with your actual site key from Google reCAPTCHA admin console
import { loadRecaptcha } from "./loadRecaptcha"

export const RECAPTCHA_SITE_KEY = "6LcMeCgsAAAAAFwBuzolv8BPj-3UJ2TGC8ebboxX"

const EXECUTE_TIMEOUT_MS = 28_000
const MAX_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1_800

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/** Wait briefly for the script in index.html to define grecaptcha (slow networks). */
async function waitForGrecaptcha(maxWaitMs = 12_000, stepMs = 100) {
  const deadline = Date.now() + maxWaitMs
  while (Date.now() < deadline) {
    if (typeof window !== "undefined" && window.grecaptcha && typeof window.grecaptcha.ready === "function") {
      return true
    }
    await sleep(stepMs)
  }
  return false
}

function isRetryableRecaptchaError(error) {
  const msg = (error && (error.message || String(error))) || ""
  const s = msg.toLowerCase()
  // Google v3: "Timeout (b)", network-ish, or our wrapper timeout
  return (
    s.includes("timeout") ||
    s.includes("(b)") ||
    s.includes("network") ||
    s.includes("failed to fetch") ||
    msg === "reCAPTCHA token request timed out" ||
    msg === "reCAPTCHA is not loaded"
  )
}

/**
 * Single execute with a hard timeout so Google's internal timeout does not surface as uncaught noise as often.
 */
function normalizeRecaptchaError(error) {
  const msg = (error && (error.message || String(error))) || "reCAPTCHA failed"
  if (/timeout/i.test(msg)) {
    return new Error("reCAPTCHA token request timed out")
  }
  return error instanceof Error ? error : new Error(msg)
}

function executeRecaptchaOnce(action) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("reCAPTCHA token request timed out"))
    }, EXECUTE_TIMEOUT_MS)

    const done = (err, token) => {
      clearTimeout(timer)
      if (err != null) reject(normalizeRecaptchaError(err))
      else resolve(token)
    }

    try {
      window.grecaptcha.ready(() => {
        try {
          const executePromise = window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
          if (!executePromise || typeof executePromise.then !== "function") {
            done(new Error("reCAPTCHA execute unavailable"))
            return
          }
          executePromise.then((token) => done(null, token)).catch((e) => done(e))
        } catch (e) {
          done(e)
        }
      })
    } catch (e) {
      clearTimeout(timer)
      reject(normalizeRecaptchaError(e))
    }
  })
}

/**
 * Generate a reCAPTCHA v3 token for the specified action.
 * Retries on timeout / network-style failures (common cause of "Timeout (b)").
 *
 * @param {string} action - e.g. 'signup', 'sendotp'
 * @returns {Promise<string>}
 */
export async function generateRecaptchaToken(action = "submit") {
  try {
    await loadRecaptcha(RECAPTCHA_SITE_KEY)
  } catch {
    throw new Error("reCAPTCHA is not loaded")
  }

  const ready = await waitForGrecaptcha()
  if (!ready) {
    throw new Error("reCAPTCHA is not loaded")
  }

  let lastError = null
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await executeRecaptchaOnce(action)
    } catch (error) {
      lastError = error
      if (attempt < MAX_ATTEMPTS && isRetryableRecaptchaError(error)) {
        await sleep(RETRY_BASE_DELAY_MS * attempt)
        continue
      }
      throw error
    }
  }
  throw lastError || new Error("reCAPTCHA failed")
}

/**
 * Check if reCAPTCHA is loaded and ready
 * @returns {boolean}
 */
export const isRecaptchaReady = () => {
  return typeof window.grecaptcha !== "undefined" && typeof window.grecaptcha.ready === "function"
}
